import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
    Box,
    Paper,
    Typography,
    Grid,
    TextField,
    Button,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Checkbox,
    CircularProgress,
    Snackbar,
    Alert,
    IconButton,
    MenuItem,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";

import InventoryServices from "../../services/InventoryServices";

const CommercialActivationRequest = () => {
    const navigate = useNavigate();
const [period, setPeriod] = useState("1_year");
    /* -----------------------------
        States
    ------------------------------*/

    const [loading, setLoading] = useState(false);

    const [rows, setRows] = useState([]);

    const [search, setSearch] = useState("");

    const [page, setPage] = useState(1);

    const [pageSize] = useState(50);

    const [totalPages, setTotalPages] = useState(1);

    const [selectedRows, setSelectedRows] = useState([]);

    const [snackbar, setSnackbar] = useState({
        open: false,
        severity: "success",
        message: "",
    });

    const showSnackbar = (
        message,
        severity = "success"
    ) => {
        setSnackbar({
            open: true,
            severity,
            message,
        });
    };

    /* -----------------------------
        Load List
    ------------------------------*/

    useEffect(() => {
        getCommercialList();
    }, [page]);

    /* -----------------------------
        API
    ------------------------------*/

    const getCommercialList = async () => {
        try {
            setLoading(true);

            const payload = {
                page,
                page_size: pageSize,
            };

            if (search.trim()) {
                payload.search = search.trim();
            }

            const response =
                await InventoryServices.commercialList(
                    payload
                );

            const result = response.data;

            const list =
                result.results ||
                result.data ||
                result.rows ||
                [];

            setRows(list);

            const total =
                result.total_pages ||
                Math.ceil(
                    (result.count ||
                        list.length ||
                        0) / pageSize
                ) ||
                1;

            setTotalPages(total);
        } catch (error) {
            showSnackbar(
                error?.response?.data?.message ||
                    "Unable to load stock.",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    /* -----------------------------
        Search
    ------------------------------*/

    const handleSearch = () => {
        setPage(1);
        getCommercialList();
    };

    const handleReset = () => {
    setSearch("");
    setPage(1);

    setTimeout(() => {
        getCommercialList();
    }, 0);
};

    /* -----------------------------
        Row Selection
    ------------------------------*/

    const handleSelectRow = (row) => {
        const exists = selectedRows.find(
            (item) => item.id === row.id
        );

        if (exists) {
            setSelectedRows(
                selectedRows.filter(
                    (item) => item.id !== row.id
                )
            );
        } else {
            setSelectedRows([
                ...selectedRows,
                row,
            ]);
        }
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedRows(rows);
        } else {
            setSelectedRows([]);
        }
    };

    const handleCreateRequest = async () => {

    if (!selectedRows.length) {
        showSnackbar(
            "Please select at least one device.",
            "warning"
        );
        return;
    }

    try {

        setLoading(true);

        const payload = {
            ids: selectedRows.map(item => item.id),
            period,
        };

        await InventoryServices.commercialCreate(payload);

        showSnackbar(
            "Commercial request created successfully."
        );

        setTimeout(() => {

            navigate("/commercial");

        }, 1000);

    } catch (error) {

        showSnackbar(
            error?.response?.data?.message ||
            "Failed to create request.",
            "error"
        );

    } finally {

        setLoading(false);

    }

};

    return (
        <Box p={3}>

            {/* Header */}

            <Box
                display="flex"
                alignItems="center"
                mb={3}
            >
                <IconButton
                    onClick={() => navigate(-1)}
                >
                    <ArrowBackIcon />
                </IconButton>

                <Typography
                    variant="h5"
                    fontWeight={700}
                >
                    Commercial Activation 
                </Typography>
            </Box>

            {/* Top Card */}

            <Paper
                elevation={2}
                sx={{
                    p: 3,
                    borderRadius: 3,
                }}
            >
                <Typography
                    variant="h6"
                    fontWeight={700}
                    color="primary"
                    mb={2}
                >
                    Dispatched stock awaiting activation (
                    {rows.length} total)
                </Typography>

                <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    mb={2}
                >
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="search imei / iccid / esn / msisdn ..."
                            value={search}
                            onChange={(e) =>
                                setSearch(
                                    e.target.value
                                )
                            }
                        />
                    </Grid>

                    <Grid item>
                        <Button
                            variant="contained"
                            startIcon={
                                <SearchIcon />
                            }
                            onClick={
                                handleSearch
                            }
                        >
                            Search
                        </Button>
                    </Grid>

                    <Grid item>
                        <Button
                            variant="outlined"
                            onClick={
                                handleReset
                            }
                        >
                            Reset
                        </Button>
                    </Grid>
                </Grid>

                <TableContainer>
                    <Table size="small">

                        <TableHead>

                            <TableRow>

                                <TableCell padding="checkbox">

                                    <Checkbox
                                        checked={
                                            rows.length > 0 &&
                                            selectedRows.length ===
                                                rows.length
                                        }
                                        indeterminate={
                                            selectedRows.length >
                                                0 &&
                                            selectedRows.length <
                                                rows.length
                                        }
                                        onChange={(e) =>
                                            handleSelectAll(
                                                e.target
                                                    .checked
                                            )
                                        }
                                    />

                                </TableCell>

                                <TableCell>
                                    <b>ID</b>
                                </TableCell>

                                <TableCell>
                                    <b>IMEI</b>
                                </TableCell>

                                <TableCell>
                                    <b>ICCID</b>
                                </TableCell>

                                <TableCell>
                                    <b>DEVICE ESN</b>
                                </TableCell>

                                <TableCell>
                                    <b>PRIMARY MSISDN</b>
                                </TableCell>

                                <TableCell>
                                    <b>STOCK STATUS</b>
                                </TableCell>

                            </TableRow>

                        </TableHead>

                        <TableBody>

                            {loading ? (

                                <TableRow>
                                    <TableCell
                                        align="center"
                                        colSpan={7}
                                        sx={{
                                            py: 5,
                                        }}
                                    >
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>

                            ) : rows.length === 0 ? (

                                <TableRow>
                                    <TableCell
                                        align="center"
                                        colSpan={7}
                                    >
                                        No Data Found
                                    </TableCell>
                                </TableRow>

                            ) : (

                                rows.map((row) => (
                                    <TableRow
                                        hover
                                        key={
                                            row.id
                                        }
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedRows.some(
                                                    (
                                                        item
                                                    ) =>
                                                        item.id ===
                                                        row.id
                                                )}
                                                onChange={() =>
                                                    handleSelectRow(
                                                        row
                                                    )
                                                }
                                            />
                                        </TableCell>

                                        <TableCell>
                                            {row.id}
                                        </TableCell>

                                        <TableCell>
                                            {row.imei ||
                                                "-"}
                                        </TableCell>

                                        <TableCell>
                                            {row.iccid_primary ||
                                                "-"}
                                        </TableCell>

                                        <TableCell>
                                            {row.device_esn ||
                                                "-"}
                                        </TableCell>

                                        <TableCell>
                                            {row.msisdn_primary ||
                                                "-"}
                                        </TableCell>

                                        <TableCell>
                                            {row.stock_upload_status ||
                                                "-"}
                                        </TableCell>
                                    </TableRow>
                                ))

                            )}

                        </TableBody>

                    </Table>
                </TableContainer>

                <Box
    mt={2}
    display="flex"
    alignItems="center"
    gap={2}
>

    <Button
        variant="outlined"
        disabled={page === 1}
        onClick={() => setPage(page - 1)}
    >
        ← Prev
    </Button>

    <Typography>

        Page {page} of {totalPages}

    </Typography>

    <Button
        variant="outlined"
        disabled={page === totalPages}
        onClick={() => setPage(page + 1)}
    >
        Next →
    </Button>

</Box>
            </Paper>
<Paper
    elevation={2}
    sx={{
        mt: 3,
        p: 3,
        borderRadius: 3,
    }}
>

    <Typography
        variant="h6"
        fontWeight={700}
        color="primary"
        mb={3}
    >
        Send activation command
    </Typography>

    <Grid
        container
        spacing={2}
        alignItems="center"
    >

        <Grid
            item
            xs={12}
            md={2}
        >

            <TextField
                select
                fullWidth
                size="small"
                value={period}
                onChange={(e) =>
                    setPeriod(e.target.value)
                }
            >

                <MenuItem value="1_year">
                    1 Year
                </MenuItem>

                <MenuItem value="2_year">
                    2 Year
                </MenuItem>

            </TextField>

        </Grid>

        <Grid item>

            <Button
                variant="contained"
                size="large"
                // disabled={
                //     loading ||
                //     selectedRows.length === 0
                // }
                onClick={handleCreateRequest}
            >

                {loading
                    ? "Creating..."
                    : "Create Commercial Request"}

            </Button>

        </Grid>

        <Grid item>

            <Typography
                color="text.secondary"
            >
                Selected :
                <strong>
                    {" "}
                    {selectedRows.length}
                </strong>
            </Typography>

        </Grid>

    </Grid>

</Paper>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                onClose={() =>
                    setSnackbar({
                        ...snackbar,
                        open: false,
                    })
                }
            >
                <Alert
                    severity={
                        snackbar.severity
                    }
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

        </Box>
    );
};

export default CommercialActivationRequest;